/**
 * Tooltip
 *
 * Inspired by: shadcn/ui, Linear, HIG (Apple Human Interface Guidelines)
 * Hover and focus hint text that appears near a trigger element.
 * Provides brief supplementary information without cluttering the interface.
 * Keyboard accessible via focus — not hover-only.
 *
 * Dependencies: @radix-ui/react-tooltip, clsx / tailwind-merge (cn utility)
 *
 * Copy this file to src/components/ui/Tooltip.tsx
 */

import * as React from "react"
import * as TooltipPrimitive from "@radix-ui/react-tooltip"
import { cn } from "@/lib/utils"

export const TooltipProvider = TooltipPrimitive.Provider

// ── Genome sources ────────────────────────────────────────────────────────────
// Block:    blocks/Tooltip/meta.yaml
//
// INVARIANTS:
//   Content: rounded-md shadow-sm z-60
//   Style: bg-foreground text-background text-sm px-3 py-1.5
//   Width: max-w-[220px] (max-w-56 = 224px, closest standard value)

interface TooltipProps {
  content: React.ReactNode
  children: React.ReactNode
  side?: "top" | "right" | "bottom" | "left"
  /** Pixel offset from trigger */
  sideOffset?: number
  /** Alignment along the side axis */
  align?: "start" | "center" | "end"
  /** Offset along the alignment axis */
  alignOffset?: number
  /** Open delay in ms (default 400ms to prevent accidental triggers) */
  delayDuration?: number
  /** Reduced delay when moving between tooltips (default 100) */
  skipDelayDuration?: number
  /** Controlled open state */
  open?: boolean
  /** Callback when open state changes */
  onOpenChange?: (open: boolean) => void
  /** Start in open state (default false) */
  defaultOpen?: boolean
  className?: string
}

export function Tooltip({
  content,
  children,
  side = "top",
  sideOffset = 4,
  align,
  alignOffset,
  delayDuration = 400,
  skipDelayDuration = 100,
  open,
  onOpenChange,
  defaultOpen,
  className,
}: TooltipProps) {
  return (
    <TooltipPrimitive.Provider
      delayDuration={delayDuration}
      skipDelayDuration={skipDelayDuration}
    >
      <TooltipPrimitive.Root
        open={open}
        onOpenChange={onOpenChange}
        defaultOpen={defaultOpen}
      >
        <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal>
          <TooltipPrimitive.Content
            side={side}
            sideOffset={sideOffset}
            align={align}
            alignOffset={alignOffset}
            className={cn(
              "z-50 overflow-hidden rounded-md shadow-sm",
              "bg-foreground text-background text-sm px-3 py-1.5",
              "max-w-56",
              "animate-in fade-in-0 zoom-in-95",
              "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
              "data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2",
              "data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
              className,
            )}
          >
            {content}
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  )
}
